'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/** Musgo de firma del tema barro (blob del kicker). */
const MOSS = '#737049'

/**
 * Hero del tema "barro" (orgánico de autor): asimetría deliberada — texto a la
 * izquierda, nunca centrado — con el nombre en minúsculas y la ÚLTIMA palabra
 * en itálica (Instrument Serif). La foto va a la derecha bajo una máscara blob
 * (border-radius orgánico irregular) y el kicker lleva un blob de musgo. Los
 * CTAs también van en minúsculas: el tema baja la voz a propósito. La barra
 * superior usa modo sólido (navSolid). Lee tokens del tema (--ms-*).
 */
export function HeroBarro({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  // Última palabra en itálica; con nombre de una sola palabra va toda en itálica.
  const words = businessName.trim().split(/\s+/)
  const lastWord = words.pop() ?? businessName
  const leadWords = words.join(' ')

  return (
    <section className="w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* pt despeja la barra superior fija (navSolid) */}
      <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-24 sm:pt-28 pb-14 sm:pb-16">
        <div className={`grid items-center gap-10 sm:gap-12 ${hasMedia ? 'md:grid-cols-[1.1fr_0.9fr]' : ''}`}>
          {/* Columna texto — asimétrica, nunca centrada */}
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 mb-5 text-[12px] lowercase tracking-wide" style={{ color: 'var(--ms-text-muted)' }}>
              <span
                className="inline-block w-[14px] h-[12px]"
                style={{ background: MOSS, borderRadius: '55% 45% 60% 40% / 50% 55% 45% 60%' }}
                aria-hidden
              />
              {city && <span>{city}</span>}
            </div>

            <h1
              className="lowercase leading-[1.0] tracking-[-0.01em] text-[clamp(42px,7.5vw,76px)]"
              style={{ fontFamily: 'var(--ms-font-display)', color: 'var(--ms-text)' }}
            >
              {leadWords ? <>{leadWords} </> : null}
              <i>{lastWord}</i>
            </h1>

            {tagline && (
              <p className="mt-4 max-w-lg text-[16px] sm:text-[17px] lowercase leading-relaxed" style={{ color: 'var(--ms-text-muted)' }}>
                {tagline}
              </p>
            )}

            {/* Meta discreta */}
            <div
              className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]"
              style={{ color: 'var(--ms-text-muted)' }}
            >
              {statusLabel && (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="w-[7px] h-[6px]"
                    style={{
                      borderRadius: '55% 45% 60% 40% / 50% 55% 45% 60%',
                      background:
                        statusLabel.tone === 'open'
                          ? '#8faa62'
                          : statusLabel.tone === 'soon'
                            ? '#f59e0b'
                            : 'var(--ms-text-muted)',
                    }}
                  />
                  {statusLabel.text}
                </span>
              )}
              {statusLabel && <span aria-hidden>·</span>}
              <span>{t('pickupAvailable')}</span>
              {pickupWaitMinutes ? (
                <>
                  <span aria-hidden>·</span>
                  <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
                </>
              ) : null}
            </div>

            {/* CTAs en minúsculas */}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onViewMenu}
                className="inline-flex items-center rounded-full px-8 py-3.5 text-[15px] font-semibold lowercase transition hover:brightness-110 active:scale-[0.98]"
                style={{
                  background: 'var(--ms-accent)',
                  color: '#f7efe3',
                  boxShadow: '0 14px 30px -14px color-mix(in srgb, var(--ms-accent) 70%, transparent)',
                }}
              >
                {t('orderOnline')}
              </button>
              {hasDelivery && onDelivery && (
                <button
                  type="button"
                  onClick={onDelivery}
                  className="inline-flex items-center rounded-full px-6 py-3.5 text-[14.5px] font-semibold lowercase transition hover:bg-black/5"
                  style={{ color: 'var(--ms-text-soft)', border: '1.5px solid var(--ms-text-soft)' }}
                >
                  {t('delivery')}
                </button>
              )}
            </div>
          </div>

          {/* Columna foto: máscara blob orgánica */}
          {hasMedia && (
            <div
              className="relative w-full overflow-hidden min-h-[300px]"
              style={{
                aspectRatio: '4 / 4.4',
                borderRadius: '58% 42% 55% 45% / 48% 58% 42% 52%',
                boxShadow: 'var(--ms-card-shadow)',
              }}
            >
              {heroVideoUrl ? (
                <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
              ) : heroImageUrl ? (
                <Image src={heroImageUrl} alt={businessName} fill priority sizes="(max-width: 768px) 100vw, 45vw" className="object-cover" />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
